import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { playerDetails } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { isGameLeft } from 'lib/so5';

import { InlineGame } from '../InlineGame';
import { So5AppearenceInlineGames_so5Appearance } from './__generated__/index.graphql';

const Chip = styled(Horizontal).attrs({ gap: 0 })`
  background-color: var(--c-nd-150);
  border-radius: var(--double-unit);
  padding: 0 var(--unit);
`;

type Props = { so5Appearance: So5AppearenceInlineGames_so5Appearance };

export const So5AppearenceInlineGames = ({ so5Appearance }: Props) => {
  const { eligiblePlayerGameScores, scoreStatus, playedGames } = so5Appearance;

  const leftGames = eligiblePlayerGameScores.filter(game =>
    isGameLeft(game.anyGame.statusTyped)
  );

  if (
    eligiblePlayerGameScores.length === 1 &&
    isType(eligiblePlayerGameScores[0].anyGame, 'Game')
  ) {
    return <InlineGame game={eligiblePlayerGameScores[0].anyGame} />;
  }
  if (leftGames.length === 1) {
    return <InlineGame game={leftGames[0].anyGame} />;
  }
  if (scoreStatus !== ScoreStatus.PENDING) {
    return null;
  }

  const hasPlayedGames = playedGames.length > 0;

  return (
    <Chip>
      <LabelXS as="p">
        <FormattedMessage
          {...(hasPlayedGames
            ? playerDetails.gamesLeftCount
            : playerDetails.gamesCount)}
          values={{
            count: leftGames.length,
          }}
        />
      </LabelXS>
    </Chip>
  );
};

So5AppearenceInlineGames.fragments = {
  so5Appearance: gql`
    fragment So5AppearenceInlineGames_so5Appearance on So5AppearanceInterface {
      id
      scoreStatus
      eligiblePlayerGameScores {
        id
        anyGame {
          id
          statusTyped
          ...InlineGame_game
        }
      }
      playedGames {
        id
      }
    }
    ${InlineGame.fragments.game}
  ` as TypedDocumentNode<So5AppearenceInlineGames_so5Appearance>,
};
